import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@mono/ui-components';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChatView } from '../components/ChatView';
import { ChatHistoryUnavailable } from '../components/ChatView/ChatRecovery';
import { PageSectionErrorBoundary } from '../components/layout';
import { useChatHistory, useConversation } from '../hooks/useConversation';
import { useLastChatId } from '../hooks/useLastChatId';
import { useLayoutStore } from '../lib/stores/LayoutStore';
export default function ChatPage() {
    const { id: chatId } = useParams();
    const navigate = useNavigate();
    const { setLastChatId, clearLastChatId } = useLastChatId();
    const [newChatId] = useState(()=>chatId && sessionStorage.getItem(`init::${chatId}`) !== null ? chatId : undefined);
    const [acknowledgedChatId, setAcknowledgedChatId] = useState();
    const startingNewChat = !!chatId && chatId === newChatId && chatId !== acknowledgedChatId;
    const conversation = useConversation(startingNewChat ? '' : chatId ?? '');
    const title = conversation.data?.conversation?.title || 'Chat';
    useEffect(()=>{
        const previous = document.title;
        document.title = `${title} · Specra`;
        return ()=>{
            document.title = previous;
        };
    }, [
        title
    ]);
    // A conversation is a reading surface: fold the app rail on the way in and
    // restore it on the way out, but only if this page folded it.
    useEffect(()=>{
        const { sidebarCollapsed, setSidebarCollapsed } = useLayoutStore.getState();
        if (sidebarCollapsed) return;
        setSidebarCollapsed(true);
        return ()=>setSidebarCollapsed(false);
    }, []);
    // Keep this tab's conversation history separate from other browser tabs.
    useEffect(()=>{
        if (chatId) setLastChatId(chatId);
    }, [
        chatId,
        setLastChatId
    ]);
    // Close the chat and clear this tab's remembered conversation.
    const handleClose = ()=>{
        clearLastChatId();
        navigate('/assistant');
    };
    const history = useChatHistory(chatId, startingNewChat);
    const { data, isLoading } = history;
    const initialMessages = data?.messages ?? [];
    const projectId = data?.projectId;
    // Loading the first saved history must not unmount the live stream that
    // created it. Existing conversations still wait for their initial history.
    if (isLoading && chatId !== newChatId) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex items-center justify-center h-full",
            children: /*#__PURE__*/ _jsx("div", {
                className: "animate-spin rounded-full h-8 w-8 border-b-2 border-primary"
            })
        });
    }
    if (history.isError) {
        return /*#__PURE__*/ _jsx(ChatHistoryUnavailable, {
            error: history.error,
            onRetry: ()=>void history.refetch()
        });
    }
    return /*#__PURE__*/ _jsx(PageSectionErrorBoundary, {
        sectionName: "Chat",
        fallback: /*#__PURE__*/ _jsx(ChatErrorFallback, {}),
        children: /*#__PURE__*/ _jsx(ChatView, {
            chatId: chatId,
            projectId: projectId,
            initialMessages: initialMessages,
            readOnly: data?.readOnly ?? false,
            variant: "specra",
            onConversationCreated: ()=>setAcknowledgedChatId(chatId),
            onClose: handleClose
        }, chatId)
    });
}
/**
 * Fallback UI shown when chat components crash.
 */ function ChatErrorFallback() {
    return /*#__PURE__*/ _jsx("div", {
        className: "flex-1 flex items-center justify-center",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "text-center p-6",
            children: [
                /*#__PURE__*/ _jsx(AlertTriangle, {
                    className: "h-10 w-10 text-amber-500 mx-auto mb-3"
                }),
                /*#__PURE__*/ _jsx("h2", {
                    className: "text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2",
                    children: "Something went wrong"
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-sm text-gray-500 dark:text-gray-400 mb-4",
                    children: "The chat encountered an error. Please try refreshing the page."
                }),
                /*#__PURE__*/ _jsxs(Button, {
                    variant: "outline",
                    onClick: ()=>window.location.reload(),
                    children: [
                        /*#__PURE__*/ _jsx(RefreshCw, {
                            className: "w-4 h-4 mr-2"
                        }),
                        "Refresh page"
                    ]
                })
            ]
        })
    });
}
